'use client';

import { useState, useCallback } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent, InvoiceData, InvoiceLine } from '@/lib/tools/types';
import { FileText, Plus, Trash2, RotateCcw, ArrowRight, Download, Eye, X } from 'lucide-react';
import Link from 'next/link';
import { ToolSignupWall } from './tool-signup-wall';

interface InvoiceCreatorProps {
  locale: Locale;
  content: ToolContent;
}

// Countries list: Netherlands first, then EU, Turkey, EU trade partners
const COUNTRIES = [
  // Netherlands always first
  { code: 'NL', name: 'Netherlands' },
  // EU Member States (alphabetically)
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  // Turkey
  { code: 'TR', name: 'Turkey' },
  // EU Trade Partners (EEA & Key Partners)
  { code: 'GB', name: 'United Kingdom' },
  { code: 'NO', name: 'Norway' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'IS', name: 'Iceland' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'JP', name: 'Japan' },
  { code: 'AU', name: 'Australia' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'UA', name: 'Ukraine' },
];

const VAT_RATES = [21, 9, 0];

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function InvoiceCreator({ locale, content }: InvoiceCreatorProps) {
  const labels = content.labels;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [invoice, setInvoice] = useState<InvoiceData>({
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'EUR',
    sellerName: '',
    sellerAddress: '',
    sellerCity: '',
    sellerPostalCode: '',
    sellerCountry: 'NL',
    sellerVatNumber: '',
    sellerCocNumber: '',
    sellerIban: '',
    sellerEmail: '',
    buyerName: '',
    buyerAddress: '',
    buyerCity: '',
    buyerPostalCode: '',
    buyerCountry: 'NL',
    buyerEmail: '',
    lines: [
      {
        id: generateId(),
        description: '',
        quantity: 1,
        unitPrice: 0,
        vatRate: 21,
        lineTotal: 0,
        vatAmount: 0,
        ean: '',
      },
    ],
    subtotal: 0,
    vatAmount: 0,
    total: 0,
    notes: '',
    paymentTerms: '',
  });

  // Save invoice to database
  const saveInvoice = async (format: 'pdf' | 'ubl') => {
    try {
      await fetch('/api/tools/invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_number: invoice.invoiceNumber,
          invoice_date: invoice.invoiceDate,
          due_date: invoice.dueDate,
          currency: invoice.currency,
          locale: locale,
          seller_name: invoice.sellerName,
          seller_address: invoice.sellerAddress,
          seller_city: invoice.sellerCity,
          seller_postal_code: invoice.sellerPostalCode,
          seller_country: invoice.sellerCountry,
          seller_vat_number: invoice.sellerVatNumber,
          seller_coc_number: invoice.sellerCocNumber,
          seller_iban: invoice.sellerIban,
          seller_email: invoice.sellerEmail,
          buyer_name: invoice.buyerName,
          buyer_address: invoice.buyerAddress,
          buyer_city: invoice.buyerCity,
          buyer_postal_code: invoice.buyerPostalCode,
          buyer_country: invoice.buyerCountry,
          buyer_email: invoice.buyerEmail,
          subtotal: invoice.subtotal,
          vat_amount: invoice.vatAmount,
          total: invoice.total,
          lines: invoice.lines.map(line => ({
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            vatRate: line.vatRate,
            lineTotal: line.lineTotal,
            vatAmount: line.vatAmount,
            ean: line.ean || null,
          })),
          notes: invoice.notes,
          payment_terms: invoice.paymentTerms,
          download_format: format,
          referrer: typeof document !== 'undefined' ? document.referrer : null,
        }),
      });
    } catch (error) {
      console.error('Failed to save invoice:', error);
    }
  };

  const [showPreview, setShowPreview] = useState(false);

  const updateLine = useCallback((id: string, field: keyof InvoiceLine, value: number | string) => {
    setInvoice(prev => {
      const newLines = prev.lines.map(line => {
        if (line.id !== id) return line;
        const updated = { ...line, [field]: value };
        // Recalculate line totals
        const lineTotal = updated.quantity * updated.unitPrice;
        const vatAmount = lineTotal * (updated.vatRate / 100);
        return { ...updated, lineTotal, vatAmount };
      });

      // Recalculate totals
      const subtotal = newLines.reduce((sum, line) => sum + line.lineTotal, 0);
      const vatAmount = newLines.reduce((sum, line) => sum + line.vatAmount, 0);
      const total = subtotal + vatAmount;

      return { ...prev, lines: newLines, subtotal, vatAmount, total };
    });
  }, []);

  const addLine = () => {
    setInvoice(prev => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          id: generateId(),
          description: '',
          quantity: 1,
          unitPrice: 0,
          vatRate: 21,
          lineTotal: 0,
          vatAmount: 0,
          ean: '',
        },
      ],
    }));
  };

  const removeLine = (id: string) => {
    setInvoice(prev => {
      if (prev.lines.length <= 1) return prev;
      const newLines = prev.lines.filter(line => line.id !== id);
      const subtotal = newLines.reduce((sum, line) => sum + line.lineTotal, 0);
      const vatAmount = newLines.reduce((sum, line) => sum + line.vatAmount, 0);
      const total = subtotal + vatAmount;
      return { ...prev, lines: newLines, subtotal, vatAmount, total };
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: invoice.currency,
    }).format(value);
  };

  const generateUbl = (): string => {
    return `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
         xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
         xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
  <cbc:CustomizationID>urn:cen.eu:en16931:2017</cbc:CustomizationID>
  <cbc:ID>${invoice.invoiceNumber}</cbc:ID>
  <cbc:IssueDate>${invoice.invoiceDate}</cbc:IssueDate>
  <cbc:DueDate>${invoice.dueDate}</cbc:DueDate>
  <cbc:InvoiceTypeCode>380</cbc:InvoiceTypeCode>
  <cbc:DocumentCurrencyCode>${invoice.currency}</cbc:DocumentCurrencyCode>
  ${invoice.notes ? `<cbc:Note>${invoice.notes}</cbc:Note>` : ''}

  <cac:AccountingSupplierParty>
    <cac:Party>
      <cac:PartyName><cbc:Name>${invoice.sellerName}</cbc:Name></cac:PartyName>
      <cac:PostalAddress>
        <cbc:StreetName>${invoice.sellerAddress}</cbc:StreetName>
        <cbc:CityName>${invoice.sellerCity}</cbc:CityName>
        <cbc:PostalZone>${invoice.sellerPostalCode}</cbc:PostalZone>
        <cac:Country><cbc:IdentificationCode>${invoice.sellerCountry}</cbc:IdentificationCode></cac:Country>
      </cac:PostalAddress>
      ${invoice.sellerVatNumber ? `<cac:PartyTaxScheme>
        <cbc:CompanyID>${invoice.sellerVatNumber}</cbc:CompanyID>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:PartyTaxScheme>` : ''}
      ${invoice.sellerEmail ? `<cac:Contact><cbc:ElectronicMail>${invoice.sellerEmail}</cbc:ElectronicMail></cac:Contact>` : ''}
    </cac:Party>
  </cac:AccountingSupplierParty>

  <cac:AccountingCustomerParty>
    <cac:Party>
      <cac:PartyName><cbc:Name>${invoice.buyerName}</cbc:Name></cac:PartyName>
      <cac:PostalAddress>
        <cbc:StreetName>${invoice.buyerAddress}</cbc:StreetName>
        <cbc:CityName>${invoice.buyerCity}</cbc:CityName>
        <cbc:PostalZone>${invoice.buyerPostalCode}</cbc:PostalZone>
        <cac:Country><cbc:IdentificationCode>${invoice.buyerCountry}</cbc:IdentificationCode></cac:Country>
      </cac:PostalAddress>
    </cac:Party>
  </cac:AccountingCustomerParty>

  ${invoice.sellerIban ? `<cac:PaymentMeans>
    <cbc:PaymentMeansCode>30</cbc:PaymentMeansCode>
    <cac:PayeeFinancialAccount>
      <cbc:ID>${invoice.sellerIban}</cbc:ID>
    </cac:PayeeFinancialAccount>
  </cac:PaymentMeans>` : ''}

  <cac:TaxTotal>
    <cbc:TaxAmount currencyID="${invoice.currency}">${invoice.vatAmount.toFixed(2)}</cbc:TaxAmount>
  </cac:TaxTotal>

  <cac:LegalMonetaryTotal>
    <cbc:LineExtensionAmount currencyID="${invoice.currency}">${invoice.subtotal.toFixed(2)}</cbc:LineExtensionAmount>
    <cbc:TaxExclusiveAmount currencyID="${invoice.currency}">${invoice.subtotal.toFixed(2)}</cbc:TaxExclusiveAmount>
    <cbc:TaxInclusiveAmount currencyID="${invoice.currency}">${invoice.total.toFixed(2)}</cbc:TaxInclusiveAmount>
    <cbc:PayableAmount currencyID="${invoice.currency}">${invoice.total.toFixed(2)}</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>

${invoice.lines.map((line, i) => `  <cac:InvoiceLine>
    <cbc:ID>${i + 1}</cbc:ID>
    <cbc:InvoicedQuantity unitCode="C62">${line.quantity}</cbc:InvoicedQuantity>
    <cbc:LineExtensionAmount currencyID="${invoice.currency}">${line.lineTotal.toFixed(2)}</cbc:LineExtensionAmount>
    <cac:Item>
      <cbc:Name>${line.description}</cbc:Name>
      ${line.ean ? `<cac:SellersItemIdentification><cbc:ID schemeID="GTIN">${line.ean}</cbc:ID></cac:SellersItemIdentification>` : ''}
    </cac:Item>
    <cac:Price><cbc:PriceAmount currencyID="${invoice.currency}">${line.unitPrice.toFixed(2)}</cbc:PriceAmount></cac:Price>
  </cac:InvoiceLine>`).join('\n')}
</Invoice>`;
  };

  const showSuccessToast = () => {
    setToastMessage(labels.invoiceCreated || 'Invoice created');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const downloadUbl = async () => {
    // Save invoice to database
    await saveInvoice('ubl');

    const xml = generateUbl();
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoice.invoiceNumber}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSuccessToast();
  };

  const downloadPdf = async () => {
    // Save invoice to database
    await saveInvoice('pdf');

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoiceNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .title { font-size: 28px; font-weight: bold; color: #1e293b; }
    .invoice-number { font-size: 14px; color: #64748b; margin-top: 4px; }
    .dates { text-align: right; font-size: 14px; }
    .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .party { width: 45%; }
    .party-label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 8px; }
    .party-name { font-weight: bold; font-size: 16px; margin-bottom: 4px; }
    .party-detail { font-size: 14px; color: #475569; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; color: #64748b; border-bottom: 2px solid #e2e8f0; }
    td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
    .text-right { text-align: right; }
    .totals { margin-top: 20px; margin-left: auto; width: 300px; }
    .totals-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
    .totals-row.grand { border-top: 2px solid #1e293b; margin-top: 8px; padding-top: 16px; font-size: 18px; font-weight: bold; }
    .notes { margin-top: 30px; padding: 16px; background: #f8fafc; border-radius: 8px; font-size: 14px; }
    .watermark { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; display: flex; align-items: center; justify-content: space-between; }
    .watermark-left { display: flex; align-items: center; }
    .disclaimer { margin-top: 12px; padding: 12px 16px; background: #fef9ee; border: 1px solid #fde68a; border-radius: 6px; font-size: 11px; color: #92400e; line-height: 1.5; }
    @media print { .watermark { position: running(footer); } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">INVOICE</div>
      <div class="invoice-number">${invoice.invoiceNumber}</div>
    </div>
    <div class="dates">
      <div><strong>Date:</strong> ${invoice.invoiceDate}</div>
      <div><strong>Due:</strong> ${invoice.dueDate}</div>
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <div class="party-label">From</div>
      <div class="party-name">${invoice.sellerName}</div>
      <div class="party-detail">${invoice.sellerAddress}</div>
      <div class="party-detail">${invoice.sellerPostalCode} ${invoice.sellerCity}</div>
      ${invoice.sellerVatNumber ? `<div class="party-detail">VAT: ${invoice.sellerVatNumber}</div>` : ''}
      ${invoice.sellerIban ? `<div class="party-detail">IBAN: ${invoice.sellerIban}</div>` : ''}
      ${invoice.sellerEmail ? `<div class="party-detail">${invoice.sellerEmail}</div>` : ''}
    </div>
    <div class="party">
      <div class="party-label">To</div>
      <div class="party-name">${invoice.buyerName}</div>
      <div class="party-detail">${invoice.buyerAddress}</div>
      <div class="party-detail">${invoice.buyerPostalCode} ${invoice.buyerCity}</div>
      ${invoice.buyerEmail ? `<div class="party-detail">${invoice.buyerEmail}</div>` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>EAN</th>
        <th class="text-right">Qty</th>
        <th class="text-right">Price</th>
        <th class="text-right">VAT</th>
        <th class="text-right">Total</th>
      </tr>
    </thead>
    <tbody>
      ${invoice.lines.map(line => `
        <tr>
          <td>${line.description || '-'}</td>
          <td style="font-family: monospace; font-size: 12px;">${line.ean || '-'}</td>
          <td class="text-right">${line.quantity}</td>
          <td class="text-right">${formatCurrency(line.unitPrice)}</td>
          <td class="text-right">${line.vatRate}%</td>
          <td class="text-right">${formatCurrency(line.lineTotal)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Subtotal</span><span>${formatCurrency(invoice.subtotal)}</span></div>
    <div class="totals-row"><span>VAT</span><span>${formatCurrency(invoice.vatAmount)}</span></div>
    <div class="totals-row grand"><span>Total</span><span>${formatCurrency(invoice.total)}</span></div>
  </div>

  ${invoice.notes ? `<div class="notes"><strong>Notes:</strong> ${invoice.notes}</div>` : ''}

  <div class="disclaimer">${locale === 'nl' ? 'Dit document is gegenereerd met de gratis factuurtool van Winkel Factuur (winkelfactuur.nl). Het dient uitsluitend als voorbeeld en is niet bedoeld als juridisch of fiscaal advies.' : locale === 'de' ? 'Dieses Dokument wurde mit dem kostenlosen Rechnungstool von Winkel Factuur (winkelfactuur.nl) erstellt. Es dient nur als Beispiel und stellt keine rechtliche oder steuerliche Beratung dar.' : locale === 'fr' ? 'Ce document a \u00e9t\u00e9 g\u00e9n\u00e9r\u00e9 avec l\u2019outil de facturation gratuit de Winkel Factuur (winkelfactuur.nl). Il est fourni \u00e0 titre d\u2019exemple uniquement et ne constitue pas un conseil juridique ou fiscal.' : 'This document was generated with the free invoice tool by Winkel Factuur (winkelfactuur.nl). It is provided as an example only and does not constitute legal or tax advice.'}</div>

  <div class="watermark">
    <div class="watermark-left">
      <img src="https://winkelfactuur.nl/bimi.svg" alt="" width="14" height="14" style="vertical-align: middle; margin-right: 6px;" />
      <span>${locale === 'nl' ? 'Gemaakt met Winkel Factuur \u2013 winkelfactuur.nl' : locale === 'de' ? 'Erstellt mit Winkel Factuur \u2013 winkelfactuur.nl' : locale === 'fr' ? 'Cr\u00e9\u00e9 avec Winkel Factuur \u2013 winkelfactuur.nl' : 'Created with Winkel Factuur \u2013 winkelfactuur.nl'}</span>
    </div>
    <span>${locale === 'nl' ? 'Gratis tool' : locale === 'de' ? 'Kostenloses Tool' : locale === 'fr' ? 'Outil gratuit' : 'Free tool'}</span>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
    showSuccessToast();
  };

  const reset = () => {
    setInvoice({
      invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      currency: 'EUR',
      sellerName: '',
      sellerAddress: '',
      sellerCity: '',
      sellerPostalCode: '',
      sellerCountry: 'NL',
      sellerVatNumber: '',
      sellerCocNumber: '',
      sellerIban: '',
      sellerEmail: '',
      buyerName: '',
      buyerAddress: '',
      buyerCity: '',
      buyerPostalCode: '',
      buyerCountry: 'NL',
      buyerEmail: '',
      lines: [{ id: generateId(), description: '', quantity: 1, unitPrice: 0, vatRate: 21, lineTotal: 0, vatAmount: 0, ean: '' }],
      subtotal: 0,
      vatAmount: 0,
      total: 0,
      notes: '',
      paymentTerms: '',
    });
    setShowPreview(false);
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
          <FileText className="h-7 w-7 text-orange-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{content.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Invoice Header */}
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{labels.invoiceNumber}</label>
            <input type="text" value={invoice.invoiceNumber} onChange={e => setInvoice(p => ({ ...p, invoiceNumber: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{labels.invoiceDate}</label>
            <input type="date" value={invoice.invoiceDate} onChange={e => setInvoice(p => ({ ...p, invoiceDate: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{labels.dueDate}</label>
            <input type="date" value={invoice.dueDate} onChange={e => setInvoice(p => ({ ...p, dueDate: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
        </div>

        {/* Seller */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">{labels.sellerDetails}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input type="text" placeholder={labels.companyName} value={invoice.sellerName} onChange={e => setInvoice(p => ({ ...p, sellerName: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.address} value={invoice.sellerAddress} onChange={e => setInvoice(p => ({ ...p, sellerAddress: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.postalCode} value={invoice.sellerPostalCode} onChange={e => setInvoice(p => ({ ...p, sellerPostalCode: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.city} value={invoice.sellerCity} onChange={e => setInvoice(p => ({ ...p, sellerCity: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <select value={invoice.sellerCountry} onChange={e => setInvoice(p => ({ ...p, sellerCountry: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <input type="text" placeholder={labels.vatNumber} value={invoice.sellerVatNumber ?? ''} onChange={e => setInvoice(p => ({ ...p, sellerVatNumber: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.iban} value={invoice.sellerIban ?? ''} onChange={e => setInvoice(p => ({ ...p, sellerIban: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="email" placeholder={labels.email} value={invoice.sellerEmail ?? ''} onChange={e => setInvoice(p => ({ ...p, sellerEmail: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
        </div>

        {/* Buyer */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">{labels.buyerDetails}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input type="text" placeholder={labels.customerName} value={invoice.buyerName} onChange={e => setInvoice(p => ({ ...p, buyerName: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.address} value={invoice.buyerAddress} onChange={e => setInvoice(p => ({ ...p, buyerAddress: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.postalCode} value={invoice.buyerPostalCode} onChange={e => setInvoice(p => ({ ...p, buyerPostalCode: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="text" placeholder={labels.city} value={invoice.buyerCity} onChange={e => setInvoice(p => ({ ...p, buyerCity: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <select value={invoice.buyerCountry} onChange={e => setInvoice(p => ({ ...p, buyerCountry: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <input type="email" placeholder={labels.email} value={invoice.buyerEmail ?? ''} onChange={e => setInvoice(p => ({ ...p, buyerEmail: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
        </div>

        {/* Invoice Lines */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">{labels.invoiceLines}</h2>
          <div className="space-y-3">
            {invoice.lines.map((line, index) => (
              <div key={line.id} className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3">
                <div className="min-w-[180px] flex-1">
                  <label className="mb-1 block text-xs text-slate-500">{labels.description}</label>
                  <input type="text" value={line.description} onChange={e => updateLine(line.id, 'description', e.target.value)} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </div>
                <div className="w-32">
                  <label className="mb-1 block text-xs text-slate-500">{labels.ean || 'EAN'} <span className="text-slate-600">(opt.)</span></label>
                  <input type="text" value={line.ean || ''} onChange={e => updateLine(line.id, 'ean', e.target.value)} placeholder="8710123456789" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono" />
                </div>
                <div className="w-16">
                  <label className="mb-1 block text-xs text-slate-500">{labels.quantity}</label>
                  <input type="number" min="1" value={line.quantity} onChange={e => updateLine(line.id, 'quantity', parseFloat(e.target.value) || 0)} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </div>
                <div className="w-24">
                  <label className="mb-1 block text-xs text-slate-500">{labels.unitPrice}</label>
                  <input type="number" min="0" step="0.01" value={line.unitPrice} onChange={e => updateLine(line.id, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </div>
                <div className="w-16">
                  <label className="mb-1 block text-xs text-slate-500">{labels.vatRate}</label>
                  <select value={line.vatRate} onChange={e => updateLine(line.id, 'vatRate', parseInt(e.target.value))} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm">
                    {VAT_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div className="w-24 text-right">
                  <label className="mb-1 block text-xs text-slate-500">{labels.lineTotal}</label>
                  <div className="rounded bg-white px-2 py-1.5 text-sm font-medium">{formatCurrency(line.lineTotal)}</div>
                </div>
                {invoice.lines.length > 1 && (
                  <button type="button" onClick={() => removeLine(line.id)} className="mb-1 rounded p-1.5 text-slate-600 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine} className="mt-3 flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700">
            <Plus className="h-4 w-4" /> {labels.addLine}
          </button>
        </div>

        {/* Totals */}
        <div className="mb-6 rounded-lg bg-slate-50 p-4">
          <div className="flex justify-between border-b border-slate-200 pb-2 text-sm">
            <span className="text-slate-600">{labels.subtotal}</span>
            <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div className="flex justify-between border-b border-slate-200 py-2 text-sm">
            <span className="text-slate-600">{labels.vatAmount}</span>
            <span className="font-medium">{formatCurrency(invoice.vatAmount)}</span>
          </div>
          <div className="flex justify-between pt-2 text-lg font-semibold">
            <span>{labels.total}</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-slate-700">{labels.notes}</label>
          <textarea rows={2} value={invoice.notes ?? ''} onChange={e => setInvoice(p => ({ ...p, notes: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowPreview(true)} className="flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Eye className="h-4 w-4" /> {labels.preview}
          </button>
          <button type="button" onClick={downloadPdf} className="flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">
            <Download className="h-4 w-4" /> {labels.downloadPdf}
          </button>
          <button type="button" onClick={downloadUbl} className="flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-900">
            <Download className="h-4 w-4" /> {labels.downloadUbl}
          </button>
          <button type="button" onClick={reset} className="flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <RotateCcw className="h-4 w-4" /> {labels.reset}
          </button>
        </div>

        <p className="mt-4 text-xs text-slate-500">{labels.watermarkNotice}</p>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">{labels.preview}</h3>
              <button type="button" onClick={() => setShowPreview(false)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Invoice Preview Content */}
              <div className="rounded-xl border border-slate-200 bg-white p-6">
                {/* Header */}
                <div className="mb-6 flex items-start justify-between border-b border-slate-200 pb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">INVOICE</h2>
                    <p className="mt-1 font-mono text-sm text-slate-600">{invoice.invoiceNumber}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p><span className="text-slate-500">{labels.invoiceDate}:</span> <span className="font-medium">{invoice.invoiceDate}</span></p>
                    <p><span className="text-slate-500">{labels.dueDate}:</span> <span className="font-medium">{invoice.dueDate}</span></p>
                  </div>
                </div>

                {/* Parties */}
                <div className="mb-6 grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase text-slate-600">{labels.sellerDetails}</p>
                    <p className="font-semibold text-slate-900">{invoice.sellerName || '-'}</p>
                    <p className="text-sm text-slate-600">{invoice.sellerAddress}</p>
                    <p className="text-sm text-slate-600">{invoice.sellerPostalCode} {invoice.sellerCity}</p>
                    {invoice.sellerVatNumber && <p className="mt-1 text-sm text-slate-600">VAT: {invoice.sellerVatNumber}</p>}
                    {invoice.sellerIban && <p className="text-sm text-slate-600">IBAN: {invoice.sellerIban}</p>}
                    {invoice.sellerEmail && <p className="text-sm text-slate-600">{invoice.sellerEmail}</p>}
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase text-slate-600">{labels.buyerDetails}</p>
                    <p className="font-semibold text-slate-900">{invoice.buyerName || '-'}</p>
                    <p className="text-sm text-slate-600">{invoice.buyerAddress}</p>
                    <p className="text-sm text-slate-600">{invoice.buyerPostalCode} {invoice.buyerCity}</p>
                    {invoice.buyerEmail && <p className="text-sm text-slate-600">{invoice.buyerEmail}</p>}
                  </div>
                </div>

                {/* Lines Table */}
                <div className="mb-6 overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">{labels.description}</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">{labels.quantity}</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">{labels.unitPrice}</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">{labels.vatRate}</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">{labels.lineTotal}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {invoice.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="px-4 py-3 text-slate-900">{line.description || '-'}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{line.quantity}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(line.unitPrice)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{line.vatRate}%</td>
                          <td className="px-4 py-3 text-right font-medium text-slate-900">{formatCurrency(line.lineTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{labels.subtotal}</span>
                      <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{labels.vatAmount}</span>
                      <span className="font-medium">{formatCurrency(invoice.vatAmount)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-2 text-lg font-bold">
                      <span>{labels.total}</span>
                      <span className="text-brand-600">{formatCurrency(invoice.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {invoice.notes && (
                  <div className="mt-6 rounded-lg bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase text-slate-600">{labels.notes}</p>
                    <p className="mt-1 text-sm text-slate-600">{invoice.notes}</p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowPreview(false)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  {labels.reset === 'Reset' ? 'Close' : locale === 'nl' ? 'Sluiten' : locale === 'de' ? 'Schließen' : locale === 'fr' ? 'Fermer' : 'Close'}
                </button>
                <button type="button" onClick={() => { setShowPreview(false); downloadPdf(); }} className="flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">
                  <Download className="h-4 w-4" /> {labels.downloadPdf}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link href={`/${locale}/signup/`} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 hover:bg-brand-50">
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
